import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms'
import { MaskitoOptions } from '@maskito/core'
import { extend, filter, first, isObject } from 'lodash'
import moment from 'moment'

import { Clinician } from '../../model/clinician'
import { ScoreConfiguration } from '../../model/intake'
import { CustomQuestionSchema } from '../../schema'
import { phoneValidator } from '../../validators'
import { PatientIntakeFormData } from '../intake-form-data.class'

export interface QuestionOptions<T> {
  id?: string
  value?: T
  clinicianId?: string
  key?: string
  parent?: string
  label?: string
  hint?: string
  autocomplete?: string
  iconSuffix?: string
  required?: boolean
  order?: number
  controlType?: string
  ifQuestions?: QuestionBase<any>[]
  introText?: string
  flex?: string
  mask?: any
  isCustom?: boolean
  min?: any
  max?: any
  validators?: ValidatorFn[]
  options?: any
  startView?: string
  note?: string
  favorite?: boolean
  expectedAnswer?: boolean
  scorer?: ScoreConfiguration
}

export abstract class QuestionBase<T> {
  /**
   * The value type.
   * e.g. 'number', 'email', 'text', 'date'.
   */
  type: string
  id?: string // custom questions use an id
  key?: string // static questions use a key
  label: string
  placeholder?: string
  hint: string
  autocomplete: string
  iconSuffix: string
  required: boolean
  order: number
  controlType: string
  introText: string // intro text shown above if questions
  flex: string
  isCustom: boolean
  parent?: string
  min?: any
  max?: any
  validators?: ValidatorFn[]
  options?: any
  startView?: string
  note?: string
  mask: MaskitoOptions
  control: UntypedFormGroup
  ifQuestions?: QuestionBase<any>[]
  favorite?: boolean
  expectedAnswer?: boolean
  clinicianId?: string
  scorer?: ScoreConfiguration

  _value: T

  set value(value: T) {
    if (this.control) {
      this.control.controls['value'].setValue(value)
    }

    this._value = value
  }

  get value() {
    return this._value
  }

  get valid() {
    if (this.control) {
      return this.control.valid
    }
    return false
  }

  get isNotExpectedAnswer() {
    return (
      typeof this.expectedAnswer === 'boolean' &&
      typeof this.value === 'boolean' &&
      this.value !== this.expectedAnswer
    )
  }

  constructor(options: QuestionOptions<T> = {}) {
    if (options.value) {
      this.value = options.value
    }

    if (options.clinicianId) {
      this.clinicianId = options.clinicianId
    }

    this.key = options.key || ''
    this.label = options.label || ''
    this.hint = options.hint
    this.autocomplete = options.autocomplete
    this.iconSuffix = options.iconSuffix
    this.required = !!options.required
    this.order = options.order === undefined ? 1 : options.order
    this.controlType = options.controlType || ''
    this.introText = options.introText
    this.mask = options.mask
    this.isCustom = options.isCustom || false
    this.min = options.min
    this.max = options.max
    this.validators = options.validators || []
    this.id = options.id || undefined
    this.parent = options.parent || undefined
    this.options = options.options || {}
    this.flex = options.flex || 'auto'
    this.startView = options.startView
    this.note = options.note
    this.favorite = options.favorite
    this.expectedAnswer = options.expectedAnswer
    this.scorer = options.scorer
  }

  getValidators(): ValidatorFn[] {
    const validators: ValidatorFn[] = []
    if (this.required) {
      validators.push(Validators.required)
    }

    // Add phone validation on phone number fields
    if (this.key.toLowerCase().includes('phonenumber')) {
      validators.push(phoneValidator)
    }

    this.validators.forEach((validator: ValidatorFn) => {
      validators.push(validator)
    })

    return validators
  }

  valueFromData(data: PatientIntakeFormData) {
    if (this.isCustom) {
      const question = first(filter(data.customQuestions, { id: this.id }))
      if (question) {
        this.value = question.value
        this.note = question.note
        this.clinicianId = question.clinicianId
      } else {
        this.value = undefined
      }
    } else {
      if (this.key === 'dob') {
        const date: any = data[this.key]
          ? moment(data[this.key]).format('YYYY-MM-DD')
          : data[this.key]
        this.value = date
      } else {
        this.value = data[this.key]
      }
    }
  }

  getParentQuestionValue(data: PatientIntakeFormData, parentId: string) {
    const question = first(filter(data.customQuestions, { id: parentId }))
    return question ? question.value : null
  }

  toFormControls(fb: UntypedFormBuilder): { [key: string]: UntypedFormControl | UntypedFormGroup } {
    if (this.isCustom) {
      this.control = fb.group({
        value: fb.control(this.value, this.getValidators()),
        note: fb.control(this.note, []),
      })
      return {
        [this.id]: this.control,
      }
    } else {
      this.control = fb.group({
        value: fb.control(this.value, this.getValidators()),
      })
      return {
        [this.key]: this.control,
      }
    }
  }

  saveValueToData(
    data: PatientIntakeFormData,
    form: UntypedFormGroup,
    clinician: Clinician,
  ) {
    if (this.isCustom) {
      const customData: CustomQuestionSchema = {
        id: this.id,
        value: form.value[this.id].value,
        note: form.value[this.id].note,
      }

      data.saveCustomQuestion(customData, clinician.id)
    } else {
      if (this.key === 'dob') {
        const date: any = form.value[this.key].value
          ? moment(form.value[this.key].value).format('YYYY-MM-DD')
          : form.value[this.key].value
        data[this.key] = date
      } else {
        if (isObject(data[this.key])) {
          extend(data[this.key], form.value[this.key].value)
        } else {
          data[this.key] = form.value[this.key].value
        }
      }
    }
  }
}

export type Questions = QuestionBase<any>[]
